import { Button } from "@minket/ui/components/button";
import { useApplyTheme } from "@minket/ui/hooks/theme";
import { type QueryClient, useQuery } from "@tanstack/react-query";
import { createRootRouteWithContext, Link, Outlet } from "@tanstack/react-router";
import { lazy, type ReactNode, Suspense, useEffect } from "react";
import { Page, SectionFrame } from "@/components/page/page";

import { Shell } from "@/components/shell/shell";
import { isUnauthorized } from "@/lib/api";
import { sessionQuery } from "@/lib/queries";
import { hadSession, rememberSession } from "@/lib/session";

/** The admin portal's tab bar (with its open ticket count) loads only when Admin is opened. */
const AdminTabs = lazy(() =>
  import("@/components/admin/admin-tabs").then((module) => ({ default: module.AdminTabs })),
);

import { useToasterWanted } from "@/lib/toast";

export interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
  notFoundComponent: NotFound,
});

/**
 * The toaster (sonner) loads with the first toast (lib/toast.ts), not with the page. Toasts stack
 * above the setup tile while it shows (`--setup-tile-block`, components/onboarding/setup-tile.tsx).
 */
const Toaster = lazy(() =>
  import("@minket/ui/components/sonner").then((module) => ({ default: module.Toaster })),
);

const loadGatePages = () => import("@/components/shell/gate-pages");
const ShellSkeletonPage = lazy(() =>
  loadGatePages().then((module) => ({ default: module.ShellSkeleton })),
);
const SessionErrorPage = lazy(() =>
  loadGatePages().then((module) => ({ default: module.SessionErrorPage })),
);

/** The shell's shape while the session loads; its code ships with the pages for no session. */
function ShellSkeleton() {
  return (
    <Suspense fallback={<div className="min-h-dvh" aria-busy="true" />}>
      <ShellSkeletonPage />
    </Suspense>
  );
}

const Blank = () => <div className="min-h-dvh" aria-busy="true" />;

/** A session that failed for a reason other than being signed out: what happened, and retry. */
function SessionError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  return (
    <Suspense fallback={<ShellSkeleton />}>
      <SessionErrorPage error={error} onRetry={onRetry} />
    </Suspense>
  );
}

/** The two pages outside the rail shell: signing in and signing up are the same screen. */
function isAuthPath(pathname: string): boolean {
  return pathname === "/signin" || pathname === "/signup";
}

/**
 * The session gate (B12). `/signin` and `/signup` render without the shell; every other page
 * needs a session. Signed out, the API answers `{ user: null }` and the page sends the person to
 * `/signin` with the address to return to; a signed-in person with no organization starts at
 * "Name your organization". A failed session that is not a refusal gets its own page.
 */
function RootComponent() {
  const resolved = useApplyTheme();
  const toasterWanted = useToasterWanted();
  const session = useQuery({ ...sessionQuery(), staleTime: 5 * 60_000 });
  const here = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const authPage = isAuthPath(window.location.pathname);
  const retry = () => void session.refetch();
  const user = session.data?.user;
  const refused = session.isError && isUnauthorized(session.error);
  useEffect(() => {
    if (user) rememberSession(true);
    else if (user === null || refused) rememberSession(false);
  }, [user, refused]);

  let content: ReactNode;
  if (authPage) {
    content = <Outlet />;
  } else if (session.isPending) {
    content = hadSession() ? <ShellSkeleton /> : <Blank />;
  } else if (session.isError) {
    content = isUnauthorized(session.error) ? (
      <GoTo href={`/signin?return_to=${encodeURIComponent(here)}`} />
    ) : (
      <SessionError error={session.error} onRetry={retry} />
    );
  } else if (session.data.user === null) {
    content = <GoTo href={`/signin?return_to=${encodeURIComponent(here)}`} />;
  } else if (
    session.data.memberships.length === 0 &&
    window.location.pathname !== "/onboarding/organization"
  ) {
    content = <GoTo href="/onboarding/organization" />;
  } else {
    content = (
      <Shell user={session.data.user} memberships={session.data.memberships}>
        <SectionFrame
          tabs={{
            admin: (
              <Suspense fallback={null}>
                <AdminTabs />
              </Suspense>
            ),
          }}
        >
          <Outlet />
        </SectionFrame>
      </Shell>
    );
  }

  return (
    <>
      {content}
      {toasterWanted && !authPage ? (
        <Suspense fallback={null}>
          <Toaster
            theme={resolved}
            position="bottom-right"
            offset={{ bottom: "calc(24px + var(--setup-tile-block, 0px))" }}
          />
        </Suspense>
      ) : null}
    </>
  );
}

/** A full navigation to where a signed-out person belongs, keeping the page to return to. */
function GoTo({ href }: { href: string }) {
  useEffect(() => window.location.replace(href), [href]);
  return <Blank />;
}

function NotFound() {
  return (
    <Page
      title="Page not found"
      remember={false}
      description="Nothing lives at this address. Check the link, or go back to the overview."
      action={
        <Button asChild>
          <Link to="/">Go to the overview</Link>
        </Button>
      }
    >
      <p className="text-sm text-muted-foreground">
        Console addresses are lowercase single words: <code className="type-data">/emails</code>,{" "}
        <code className="type-data">/domains</code>, <code className="type-data">/keys</code>.
      </p>
    </Page>
  );
}
